import { HOST, API } from '@/utils/http'
import { v4 as uuidv4 } from 'uuid'
import { toast } from "@/components/ui/toast/use-toast"
import { getResources, uploadResource, deleteResource } from '@/services/resourcesApi'


export const fetchBeacons = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/beacons/${payload.project_id}`
    })

    for (const beacon of response.data) {
      commit('NEXT_BEACON', beacon)
    }
  } catch (e) {
    // commit('SET_ALERT', {type: 'error', text: `Can't fetch beacons`}, {root: true})
  }
}


export const fetchFeatures = async ({commit}, payload) => {
  // ugly one: isMeta is used to fetch features for the feed, it's mean to load more data
  try {
    if (payload.isMeta) {
      const response = await API({
        method: 'GET',
        url: `${HOST}/geo/features/${payload.project_id}`,
        params: payload
      })
      commit('SET_FEED_FEATURES', {
        features: response.data?.features
      })
    } else {
      const response = await API({
        method: 'GET',
        url: `${HOST}/geo/features-v2/${payload.project_id}`,
        params: payload
      })
      commit('SET_FEATURES', {
        features: response.data?.features,
        isMerge: false
      })
      commit('SET_TOTAL_ONMAP', response.data?.total)
    }
  } catch (e) {
    if (!e.response) {
      toast({ title: "Can't fetch features", variant: 'destructive' })
      return
    }

    if (e.response.status === 403) {
      toast({ title: "You don't have permission to access this project", variant: 'destructive' })
    } else {
      throw new Error(e)
    }
  }
}

export const fetchFeature = async ({commit}, code) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/feature/${code}`
    })

    const feature = response.data

    commit('SET_CURRENT_FEATURE', {feature: feature})
  } catch (e) {
    if (!e.response) {
      toast({ title: "Can't fetch report", variant: 'destructive' })
      return
    }

    if (e.response.status === 403) {
      toast({ title: "You don't have permission to access this report", variant: 'destructive' })
    }

    if (e.response.status === 404) {
      toast({ title: "Report not found", variant: 'destructive' })
    }
  }
}

export const fetchResources = async ({commit}, featureId) => {
  try {
    const response = await getResources(featureId)
    commit('SET_RESOURCES', response.resources || [])
    return response.resources
  } catch (error) {
    console.error('Failed to fetch resources:', error)
    toast({
      title: 'Failed to fetch resources',
      description: error.message,
      variant: 'destructive'
    })
    throw error
  }
}

export const uploadResourceNew = async ({commit}, payload) => {
  try {
    const response = await uploadResource(
      payload.file,
      payload.featureCode,
      payload.onProgress
    )
    // Add all resource variants to the store
    response.resources.forEach(resource => {
      commit('ADD_RESOURCE', resource)
    })
    return response
  } catch (error) {
    console.error('Failed to upload resource:', error)
    toast({
      title: 'Upload failed',
      description: error.message,
      variant: 'destructive'
    })
    throw error
  }
}

export const deleteResourceAction = async ({commit}, resourceId) => {
  try {
    await deleteResource(resourceId)
    commit('REMOVE_RESOURCE', resourceId)
    toast({
      title: 'Resource deleted',
      description: 'Resource has been successfully deleted'
    })
  } catch (error) {
    console.error('Failed to delete resource:', error)
    toast({
      title: 'Delete failed',
      description: error.message,
      variant: 'destructive'
    })
    throw error
  }
}

export const saveResource = async ({commit}, payload) => {
  try {
    const response = await API({
      headers: {'content-type': 'multipart/form-data'},
      method: 'POST',
      url: `${HOST}/geo/feature/${payload.code}/resource`,
      params: {
        uuid: payload.uuid
      },
      data: {
        file: payload.file
      }
    })
  } catch (e) {
    throw new Error(e)
  }
}

export const saveFeature = async ({commit, dispatch, state}, request) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/geo/feature?project_id=${request.project_id}`,
      data: request.feature
    })

    const feature = response.data
    const projectStr = `project-${request.project_id}-`

    if (state.map.getSource(`${projectStr}reports_points`)) {
      if (feature.geometry.type === 'Point') {
        state.map.getSource(`${projectStr}reports_points`).setData(feature)
      } else {
        state.map.getSource(`${projectStr}reports_mixed`).setData(feature)
      }
    }

    if (request.files) {
      for (const file of request.files) {
        await dispatch('saveResource', {
          code: feature.properties.code,
          file: file,
          uuid: uuidv4()
        })
      }
    }

    return feature
  } catch (e) {
    throw new Error(e)
  }
}

export const updateFeature = async ({commit, state}, payload) => {
  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/geo/feature/${payload.feature.properties.code}`,
      data: payload.feature
    })

    const feature = response.data
    const projectStr = `project-${payload.project_id}-`

    if (state.map.getSource(`${projectStr}reports_points`)) {
      if (feature.geometry.type === 'Point') {
        state.map.getSource(`${projectStr}reports_points`).setData(feature)
      } else {
        state.map.getSource(`${projectStr}reports_mixed`).setData(feature)
      }
    }

    commit('SET_CURRENT_FEATURE', {feature: feature})
    return feature
  } catch (e) {
    throw new Error(e)
  }
}

export const pairToProject = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/geo/feature/${payload.properties.code}/pair`
    })
  } catch (e) {
    throw new Error(e)
  }
}

export const updateFeatureGeometry = async ({commit}, feature) => {
  try {
    const response = await API({
      method: 'PUT',
      url: `${HOST}/geo/feature/${feature.properties.code}/geometry`,
      data: feature.geometry
    })
  } catch (e) {
    throw new Error(e)
  }
}


export const deleteFeature = async ({ commit }, code) => {
  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/geo/feature/${code}`
    })

    commit('DELETE_FEATURE', code)
  } catch(e) {
    throw new Error(e)
  }
}

// Beacons
export const fetchLatestBeacons = async ({commit}, projectId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/beacons/${projectId}`
    })

    for (const beacon of response.data) {
      commit('NEXT_BEACON', beacon)
    }
  } catch (e) {
    throw new Error(e)
  }
}

// Notes
export const fetchNotes = async ({commit}, featureCode) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/notes/${featureCode}`
    })

    commit('SET_NOTES', response.data)
  } catch (e) {
    throw new Error(e)
  }
}

export const addNote = async ({commit}, payload) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/notes/${payload.featureCode}`,
      data: {
        text: payload.text
      }
    })

    commit('ADD_NOTE', response.data)
  } catch (e) {
    throw new Error(e)
  }
}

export const deleteNote = async ({commit}, noteId) => {
  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/notes/${noteId}`
    })

    commit('DELETE_NOTE', noteId)
  } catch (e) {
    throw new Error(e)
  }
}

export const mapReady = async ({commit}, payload) => {
  commit('SET_MAP_READY', true)
}

export const map2Ready = async ({commit}, payload) => {
  commit('SET_MAP2_READY', true)
}

// Report Templates

export const createReportTemplate = async ({commit}, payload) => {
  try {
    const { template, icon } = payload

    // Use admin endpoint if project_id is undefined, otherwise use project endpoint
    const isAdminContext = template.project_id === undefined
    const createUrl = isAdminContext
      ? `${HOST}/admin/templates`
      : `${HOST}/project/${template.project_id}/templates`

    const response = await API({
      method: 'POST',
      url: createUrl,
      data: template
    })

    const newTemplate = response.data

    if (icon) {
      const formData = new FormData()
      formData.append('icon', icon)

      // Use admin endpoint for icon upload if in admin context
      const iconUrl = isAdminContext
        ? `${HOST}/admin/templates/${newTemplate.id}/upload-icon`
        : `${HOST}/project/${template.project_id}/templates/${newTemplate.id}/upload-icon`

      const response = await API({
        method: 'PUT',
        url: iconUrl,
        data: formData
      })

      commit('ADD_REPORT_TEMPLATE', response.data)
    } else {
      commit('ADD_REPORT_TEMPLATE', newTemplate)
    }

  } catch (e) {
    toast({ title: "Can't create report template", variant: 'destructive' })
  }
}

// headers: {'content-type': 'multipart/form-data'},


export const updateReportTemplate = async ({commit}, payload) => {
  try {
    const { project_id, template, icon } = payload

    // Use admin endpoint if project_id is undefined, otherwise use project endpoint
    const isAdminContext = project_id === undefined
    const updateUrl = isAdminContext
      ? `${HOST}/admin/templates/${template.id}`
      : `${HOST}/project/${project_id}/templates/${template.id}`

    const response = await API({
      method: 'PUT',
      url: updateUrl,
      data: template
    })

    const newTemplate = response.data

    if (icon) {
      const formData = new FormData()
      formData.append('icon', icon)

      // Use admin endpoint for icon upload if in admin context
      const iconUrl = isAdminContext
        ? `${HOST}/admin/templates/${newTemplate.id}/upload-icon`
        : `${HOST}/project/${project_id}/templates/${newTemplate.id}/upload-icon`

      const response = await API({
        method: 'PUT',
        url: iconUrl,
        data: formData
      })

      commit('UPDATE_REPORT_TEMPLATE', response.data)
    } else {
      commit('UPDATE_REPORT_TEMPLATE', newTemplate)
    }
  } catch (e) {
    toast({ title: "Can't update report template", variant: 'destructive' })
  }
}

export const fetchProjectReportTemplates = async ({commit}, projectId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/project/${projectId}/templates`
    })

    commit('SET_REPORT_TEMPLATES', response.data)
  } catch (e) {
    toast({ title: "Can't fetch report templates", variant: 'destructive' })
  }
}

export const fetchAllTemplates = async ({commit}, organizationId = null) => {
  try {
    if (!organizationId) {
      toast({ title: "No organization selected", variant: 'destructive' })
      return
    }

    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${organizationId}/templates`
    })

    commit('SET_ALL_TEMPLATES', response.data)
  } catch (e) {
    toast({ title: "Can't fetch templates", variant: 'destructive' })
  }
}


export const deleteReportTemplate = async ({commit}, payload) => {
  try {
    const { project_id, template } = payload

    // Use admin endpoint if project_id is undefined, otherwise use project endpoint
    const isAdminContext = project_id === undefined
    const deleteUrl = isAdminContext
      ? `${HOST}/admin/templates/${template.id}`
      : `${HOST}/project/${project_id}/templates/${template.id}`

    await API({
      method: 'DELETE',
      url: deleteUrl
    })

    commit('DELETE_REPORT_TEMPLATE', template)
  } catch (e) {
    toast({ title: "Can't delete report template", variant: 'destructive' })
  }
}

export const fetchProjectBoundary = async ({commit}, projectId) => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/project/${projectId}/boundary`
    })

    return response.data
  } catch (e) {
    if (!e.response) {
      toast({ title: "Can't fetch project boundary", variant: 'destructive' })
      return
    }

    if (e.response.status === 403) {
      toast({ title: "You don't have permission to access this project boundary", variant: 'destructive' })
    } else {
      throw new Error(e)
    }
  }
}
